import React, { FC, useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { marketplaceMatchesGridActions } from '../MarketplaceMatchesGrid/MarketplaceMatchesGridCreate';
import { addMatch, deleteMatch } from '../../MarketPricingActions';
import { addSelectedCutID, getLastModifiedDate, removeSelectedCutID } from '../../JobPricing/JobPricingActions';
import { combineData, filterPurchasedAndSearch, areSettingsEmpty, checkForNoResultsAfterSearch, genNoDataText } from './helpers';
import GG_DEFAULTS from '../../../../lib/genericGridDefaults';
import AdvancedTable from 'ps-components/AdvancedTable/AdvancedTable';
import { getGenericSurveyRow, getTransformedColumns } from 'ps-components/AdvancedTable/GenericGrid/GenericGridFunctions';
import '../MarketplaceMatchesGrid/MarketplaceMatchesGrid.scss';
import classnames from 'classnames';
import { MarketplaceMatchesSlideoutPlaceholder } from './MarketplaceMatchesSlideoutPlaceholder';
import { getSurveyDetails, setVisibleSurveysCount } from '../MarketplaceMatchesActions';
import { showSlideout } from 'ps-components/Slideout/SlideoutReducer';
import { SLIDEOUTS } from '../../../Slideout/Slideouts';
import { marketplaceMatchesGridSelector } from './selectors';
import { isAdHocJobId } from '../../helper';
import { resetSurveyJobDataCuts } from '../../SurveyJobDataCuts/SurveyJobDataCutActions';
import { AdvancedTableRowI } from '../../../../lib/types';
import { jobMatchGridActions } from '../../LandingPage/JobMatchDetail/JobMatchGridCreate'

const MarketplaceMatchesGridNew: FC = () => {
  const dispatch = useDispatch()
  const { session, matches, displayIn, selectedCutIDs, selectedViewPk, grid, loadingId, isMatchDeleting, slideout, settings, jobGridLoading, slideoutOpen } = useSelector(marketplaceMatchesGridSelector)
  const { showActiveSurveys, currentJobIndex, selectedJobIds, jobs, activeVisibleSurveysCount, suggestedVisibleSurveysCount, searchText } = slideout
  const currentJob = jobs[currentJobIndex]
  const [activeSurveys, suggestedSurveys] = [matches.purchased, matches.unpurchased]
  const { benchmarkYear, organization, currency } = session
  const { monetaryRounding, numericRounding, percentRounding } = session.user.preferences
  const { readOnlyUser } = session.user.rights
  const [addedMatches, setAddedMatches] = useState<any[]>([]);
  const [selectedSurveys, setSelectedSurveys] = useState<number[]>(selectedCutIDs);
  const [renderPopover, setRenderPopover] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState<number | undefined>();

  const surveyJobs = showActiveSurveys ? activeSurveys : suggestedSurveys;
  const options = {
    benchmarkYears: [benchmarkYear],
    displayIn: displayIn?.toLowerCase(),
    currency: currency.code,
    data: surveyJobs?.filter(j => j.purchased).map(j => ({ cutId: j.cutId }))
  };

  useEffect(() => {
    setSelectedSurveys(selectedCutIDs)
  }, [selectedCutIDs])

  useEffect(() => {
    if (addedMatches.length > 0) {
      setAddedMatches([]);
    }
  }, [selectedSurveys]);

  const shouldFetchSurveyJobs  = (activeSurveys && activeSurveys?.length > 0) && !matches.loading && !grid.loading && !grid.loaded && showActiveSurveys && !jobGridLoading
  useEffect(() => {
    if (shouldFetchSurveyJobs) {
      if (options.data?.length !== 0) {
        dispatch(marketplaceMatchesGridActions.search(`/cms/api/accounts/${organization.masterAccountId}/survey-jobs`, options, 'post'));
      }
    }
  }, [displayIn, selectedViewPk, surveyJobs, jobGridLoading]);

  const newGrid = useMemo(() => {
    return combineData(matches, grid, showActiveSurveys);
  }, [activeSurveys, suggestedSurveys, grid, showActiveSurveys]);

  const filteredData = filterPurchasedAndSearch(matches, showActiveSurveys, searchText, newGrid.data, newGrid.terms, settings)

  useEffect(() => {
    if (filteredData?.length && (showActiveSurveys && activeVisibleSurveysCount !== filteredData?.length) ||
      (!showActiveSurveys && suggestedVisibleSurveysCount !== filteredData?.length)) {
      dispatch(setVisibleSurveysCount(filteredData.length))
    }
  }, [filteredData?.length, showActiveSurveys])

  const onClickLearnMore = (rowData: any) => {
    dispatch(showSlideout(SLIDEOUTS.MARKETPLACE_MATCHES_SURVEY_DETAILS, {
      onBack: () => dispatch(showSlideout(SLIDEOUTS.MARKETPLACE_MATCHES_NEW, { selectedJobIds, startingJobIndex: currentJobIndex, dontRefetch: true })),
      isBigger: true,
      readOnly: false
    }))
    const surveyJobs = showActiveSurveys ? activeSurveys : suggestedSurveys;
    dispatch(getSurveyDetails(rowData, surveyJobs, filteredData, ''));
  }

  const updateModifiedDate = (jobId: number, benchmarkYear: number) => {
    if (!isAdHocJobId(jobId)) dispatch(getLastModifiedDate(jobId, benchmarkYear))
  }

  const handleSelect = async (cutId: number) => {
    if (isChecked(cutId)) {
      dispatch(deleteMatch(currentJob.jobId, [cutId], null, benchmarkYear, false, null, true));
      dispatch(removeSelectedCutID(cutId));

      const newAddedMatches = addedMatches;
      newAddedMatches.splice(newAddedMatches.indexOf(cutId), 1);

      setAddedMatches(newAddedMatches);
    } else {
      const surveyJobs = showActiveSurveys ? matches?.purchased : suggestedSurveys;
      const rec = surveyJobs?.find(job => job.cutId === cutId);
      let feedback;
      if (rec) {
        feedback = {
          recommendationsId: rec.recommendationsId,
          payscaleProduct: 'MarketPay',
          organizationJobTitle: rec.jobTitle,
          currency: rec.surveyCurrency,
          benchmarkYear: benchmarkYear,
          selectedMatches: [
            {
              surveyCode: rec.surveyCode,
              surveyJobCode: rec.surveyJobCode
            }
          ]
        };
      }

      dispatch(addMatch(currentJob.jobId, cutId, feedback, false, benchmarkYear, null, true));
      dispatch(addSelectedCutID(cutId));
      const newAddedMatches = addedMatches;
      newAddedMatches.push(cutId);

      setAddedMatches(newAddedMatches);
    }
    const selectedJobId = selectedJobIds[currentJobIndex]
    dispatch(jobMatchGridActions.setIsStale(selectedJobId, true))
    updateModifiedDate(currentJob?.jobId, benchmarkYear);
  }

  const useSelectAccessor = (row: AdvancedTableRowI) => row.allCells.find((cell: any) => cell.column.id === GG_DEFAULTS.CUT_ID)?.value;
  const isRowLoading = (row: AdvancedTableRowI, loadingId: string) => row.allCells.find(cell => cell.column.id === GG_DEFAULTS.CUT_ID)?.value === loadingId;
  const isChecked = (cutId: number) => selectedSurveys.includes(cutId);

  const togglePopover = (event: Event, id: number) => {
    event?.stopPropagation();
    setRenderPopover(!renderPopover);
    setSelectedRowId(id);
  }

  const handleDataCutsSlideout = (survey: any) => {
    dispatch(resetSurveyJobDataCuts());
    dispatch(showSlideout(SLIDEOUTS.SURVEY_DATA_CUTS_SLIDEOUT, {
      addSelectedCutID: dispatch(addSelectedCutID()),
      benchmarkYear,
      currentJob: { id: currentJob?.jobId, jobCode: currentJob?.code, jobCodeKey: currentJob?.codeKey },
      displayIn,
      monetaryRounding,
      onMatchSelect: () => 'refreshAfterDatacutChange',
      readOnly: readOnlyUser,
      removeSelectedCutID: () => 'jobPricingActions.removeSelectedCutID',
      survey,
      onBack: () => dispatch(showSlideout(SLIDEOUTS.MARKETPLACE_MATCHES_NEW, { selectedJobIds, startingJobIndex: currentJobIndex, dontRefetch: true }))
    }));
  }

  const counts = {
    purchased: activeSurveys?.length,
    unpurchased: suggestedSurveys?.length
  };

  const noDataText = genNoDataText(searchText, counts, showActiveSurveys)

  const getSurveyJobDetailsPageHref = (cutId: string | number) => {
    const organizationCode = organization.code
    return `${window.location.origin}/view/o/${organizationCode}/mp/iframe/merit/merit-admin/survey_lib-3?one_cut_id=${cutId}`;
  }

  const dataLoading = matches?.loading || jobGridLoading || (showActiveSurveys && (grid?.loading || (shouldFetchSurveyJobs && !grid?.loaded)))
  const isListFiltered = !areSettingsEmpty(settings) && !dataLoading
  const { noResultsAfterSearching, noResultsAfterFilter, noResultsWithoutFilters } = checkForNoResultsAfterSearch(isListFiltered, matches, grid, filteredData, searchText)
  const showPlaceholder = (noResultsAfterSearching || noResultsAfterFilter || noResultsWithoutFilters) && !dataLoading

  return (
    <div className={classnames('generic-grid__container marketplace-matches__grid-container', { 'unpurchased-grid-container': !showActiveSurveys })}>
      {showPlaceholder ? (
        <MarketplaceMatchesSlideoutPlaceholder
          noResultsAfterSearching={noResultsAfterSearching}
          noResultsWithoutFilters={noResultsWithoutFilters}
          searchText={searchText}
          showActiveSurveys={showActiveSurveys}
        />
      ) : (
        <>
          {isListFiltered && (
            <div className="filtered-indication-text">
              {`There ${filteredData.length === 1 ? 'is 1 marketplace match' : `are ${filteredData.length} marketplace matches`
                } based on applied filters`}
            </div>
          )}
          <div style={{ height: 'calc(100vh - 215px)', overflowY: 'auto' }}>
         { slideoutOpen && // Need to remove table from DOM when slideout is closed to avoid duplicate tooltips on landing page
         <AdvancedTable
            {...newGrid.tableProps}
            style={{ overflowY: 'auto' }}
            getDataRow={(row: any, idx: number) =>
              getGenericSurveyRow(row, idx, {
                onClick: onClickLearnMore,
                isRowLoading: isRowLoading(row, loadingId),
                isRowDeleting: isMatchDeleting,
                loadingText: 'Adding Match to Pricing...',
                deletingText: 'Removing Match from Pricing...',
                togglePopover,
                selectedRowId,
                newSlideout: true,
                renderPopover,
                addedMatches,
                onDataCutClick: handleDataCutsSlideout,
                getSurveyJobDetailsPageHref,
                isUnpurchased: !showActiveSurveys,
                numericRounding,
                percentRounding
              })
            }
            rowData={filteredData}
            totalCount={filteredData.length}
            columnHeaders={getTransformedColumns(
              newGrid.termGroups,
              newGrid.terms,
              filteredData,
              newGrid.defaultLeftColumns,
              newGrid.defaultRightColumns
            )}
            rowIndexPrefix={`marketplace-matches-${showActiveSurveys ? 'purchased' : 'unpurchased'}`}
            stickyHeader
            useSelectAccessor={showActiveSurveys ? useSelectAccessor : null}
            isChecked={isChecked}
            onCheck={handleSelect}
            loadingId={loadingId}
            centerAlignHeaders
            dataLoading={dataLoading}
            noDataText={noDataText}
            loadingText={'Loading Survey Job Data'}
            hideSelect={false}
          />
          }
          </div>

        </>
      )}
    </div>
  );
}

export default MarketplaceMatchesGridNew